import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Modal, Form, Select, Tag, Space,
  Card, Row, Col, Statistic, message, Tooltip, Popconfirm,
  Tabs, Drawer, Timeline,
} from 'antd';
import {
  MobileOutlined, PlusOutlined, SearchOutlined, HomeOutlined,
  EditOutlined, DeleteOutlined, LinkOutlined, HistoryOutlined,
  CheckCircleOutlined, StopOutlined, CloseCircleOutlined, DownloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin, canEdit } from '../utils/auth';
import Footer from '../components/Footer';

const { Option } = Select;

const OPERADORA_COLOR = { Claro: 'red', Tigo: 'blue', Personal: 'cyan' };
const ESTADO_COLOR    = { instalado: 'success', stock: 'warning', desinstalado: 'warning', baja: 'error' };
const ESTADO_LABEL    = { instalado: 'Instalado', stock: 'En Stock', desinstalado: 'En Stock', baja: 'Baja' };
const EVENTO_COLOR    = { asignado: 'green', reasignado: 'blue', desasignado: 'red' };

const Lineas = () => {
  const navigate = useNavigate();
  const [lineas, setLineas]     = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [gpsList, setGpsList]   = useState([]);
  const [routers, setRouters]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [tab, setTab]           = useState('rastreo');

  const [modal, setModal]       = useState({ modo: null, item: null });
  const [form]                  = Form.useForm();

  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [historialData, setHistorialData] = useState(null);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [paginas, setPaginas] = useState({ rastreo: 1, vision: 1 });
  const [pageSize, setPageSize]           = useState(20);

  const cargar = async () => {
    setLoading(true);
    try {
      const [lRes, vRes, gRes, rRes] = await Promise.all([
        axiosInstance.get('/lineas'),
        axiosInstance.get('/vehiculos'),
        axiosInstance.get('/gps'),
        axiosInstance.get('/vision/routers'),
      ]);
      setLineas(lRes.data);
      setVehiculos(vRes.data);
      setGpsList(gRes.data);
      setRouters(rRes.data);
    } catch { message.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const exportarExcel = () => {
    const toRow = l => ({
      'Número':      l.numero || '—',
      'ICCID / N°SIM': l.iccid || '—',
      'Operadora':   l.operadora || '—',
      'Tipo SIM':    l.tipo_sim === 'm2m' ? 'M2M' : 'Voz',
      'Cobertura':   l.cobertura || '—',
      'Estado':      ESTADO_LABEL[l.estado] || l.estado || '—',
      'Vehículo':    l.vehiculo_chapa || '—',
      'IMEI GPS':    l.imei || l.dispositivo || '—',
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lineas.filter(l => l.tipo === 'rastreo').map(toRow)), 'Rastreo Satelital');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lineas.filter(l => l.tipo === 'vision').map(toRow)), 'HSS Vision');
    XLSX.writeFile(wb, `HSS_Lineas_SIM_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const filtrar = (tipo) => lineas.filter(l => {
    if (l.tipo !== tipo) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return l.numero?.includes(s) || l.iccid?.includes(s) ||
      l.operadora?.toLowerCase().includes(s) ||
      l.vehiculo_chapa?.toLowerCase().includes(s) ||
      l.dispositivo?.toLowerCase().includes(s) ||
      l.nombre_gps?.toLowerCase().includes(s) ||
      l.imei?.includes(s);
  });
  const filtradas = filtrar(tab);

  const abrirModal = (modo, item = null) => {
    setModal({ modo, item });
    if (modo === 'editar' && item) {
      form.setFieldsValue(item);
    } else if (modo === 'asignar' && item) {
      form.setFieldsValue({
        vehiculo_chapa: item.vehiculo_chapa || null,
        gps_id: item.gps_id || null,
        router_id: item.router_id || null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ estado: 'instalado', tipo: tab });
    }
  };
  const cerrarModal = () => { setModal({ modo: null, item: null }); form.resetFields(); };

  const ejecutarAsignar = async (values, item) => {
    try {
      await axiosInstance.post(`/lineas/${item.id}/asignar`, {
        vehiculo_chapa: values.vehiculo_chapa || null,
        gps_id:         values.gps_id         || null,
        router_id:      values.router_id       || null,
        observacion:    values.observacion     || null,
      });
      message.success('Operación exitosa');
      cerrarModal();
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onSubmit = async (values) => {
    const { modo, item } = modal;
    try {
      if (modo === 'nuevo') {
        await axiosInstance.post('/lineas', values);
        message.success('Operación exitosa');
        cerrarModal();
        cargar();
      } else if (modo === 'editar') {
        await axiosInstance.put(`/lineas/${item.id}`, values);
        message.success('Operación exitosa');
        cerrarModal();
        cargar();
      } else if (modo === 'asignar') {
        const eraAsignada = item.vehiculo_chapa || item.gps_id || item.router_id;
        const seraAsignada = values.vehiculo_chapa || values.gps_id || values.router_id;
        if (eraAsignada && !seraAsignada) {
          Modal.confirm({
            title: 'Confirmar desasignación',
            content: `La línea ${item.numero || item.iccid} quedará sin vehículo ni dispositivo y pasará automáticamente a "En Stock". ¿Confirmar?`,
            okText: 'Sí, pasar a En Stock',
            cancelText: 'Cancelar',
            onOk: () => ejecutarAsignar(values, item),
          });
        } else {
          await ejecutarAsignar(values, item);
        }
      }
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onEliminar = async (id) => {
    try {
      await axiosInstance.delete(`/lineas/${id}`);
      message.success('Línea eliminada');
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const abrirHistorial = async (item) => {
    setDrawerOpen(true);
    setLoadingHistorial(true);
    setHistorialData(null);
    try {
      const res = await axiosInstance.get(`/lineas/${item.id}/historial`);
      setHistorialData(res.data);
    } catch { message.error('Error al cargar historial'); }
    finally { setLoadingHistorial(false); }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const filtPorTab = lineas.filter(l => l.tipo === tab);
  const stats = {
    total:      filtPorTab.length,
    activas:    filtPorTab.filter(l => l.estado === 'instalado').length,
    inactivas:  filtPorTab.filter(l => l.estado === 'stock' || l.estado === 'desinstalado').length,
    bajas:      filtPorTab.filter(l => l.estado === 'baja').length,
    claro:    filtPorTab.filter(l => l.operadora === 'Claro').length,
    tigo:     filtPorTab.filter(l => l.operadora === 'Tigo').length,
    personal: filtPorTab.filter(l => l.operadora === 'Personal').length,
    voz:      filtPorTab.filter(l => l.tipo_sim === 'voz' || !l.tipo_sim).length,
    m2m:      filtPorTab.filter(l => l.tipo_sim === 'm2m').length,
  };

  // ── Columnas ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Número', dataIndex: 'numero', key: 'numero',
      render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span>,
    },
    {
      title: 'ICCID / N°SIM', dataIndex: 'iccid', key: 'iccid',
      render: v => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: 'Operadora', dataIndex: 'operadora', key: 'operadora',
      render: op => <Tag color={OPERADORA_COLOR[op] || 'default'} style={{ fontWeight: 700 }}>{op}</Tag>,
    },
    {
      title: 'Tipo SIM', dataIndex: 'tipo_sim', key: 'tipo_sim', width: 90,
      render: t => t === 'm2m'
        ? <Tag color="purple" style={{ fontWeight: 600 }}>M2M</Tag>
        : <Tag color="green" style={{ fontWeight: 600 }}>Voz</Tag>,
    },
    ...(tab === 'rastreo' ? [{
      title: 'Cobertura', dataIndex: 'cobertura', key: 'cobertura',
      render: c => c ? <Tag color={c === 'internacional' ? 'purple' : 'geekblue'}>{c === 'internacional' ? 'Internacional' : 'Nacional'}</Tag> : '—',
    }] : []),
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado',
      render: e => <Tag color={ESTADO_COLOR[e] || 'default'}>{ESTADO_LABEL[e] || e}</Tag>,
    },
    {
      title: 'Vehículo', dataIndex: 'vehiculo_chapa', key: 'veh',
      render: chapa => chapa
        ? <span style={{ color: hssColors.primary, fontWeight: 700, cursor: 'pointer', fontFamily: 'monospace' }}
            onClick={() => navigate(`/vehiculos/${chapa}`)}>{chapa}</span>
        : <span style={{ color: hssColors.gray400 }}>Sin asignar</span>,
    },
    {
      title: tab === 'rastreo' ? 'IMEI GPS' : 'Router',
      dataIndex: 'dispositivo', key: 'disp',
      render: v => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : <span style={{ color: hssColors.gray400 }}>—</span>,
    },
    {
      title: '', key: 'acc', width: 140,
      render: (_, r) => (
        <Space>
          <Tooltip title="Asignar">
            <Button type="text" icon={<LinkOutlined />} style={{ color: hssColors.info }}
              onClick={() => abrirModal('asignar', r)} />
          </Tooltip>
          <Tooltip title="Historial">
            <Button type="text" icon={<HistoryOutlined />} style={{ color: hssColors.warning }}
              onClick={() => abrirHistorial(r)} />
          </Tooltip>
          {canEdit() && (
            <Tooltip title="Editar">
              <Button type="text" icon={<EditOutlined />} style={{ color: hssColors.primary }}
                onClick={() => abrirModal('editar', r)} />
            </Tooltip>
          )}
          {isAdmin() && (
            <Popconfirm title="¿Eliminar esta línea?" onConfirm={() => onEliminar(r.id)} okText="Sí" cancelText="No">
              <Button type="text" icon={<DeleteOutlined />} style={{ color: hssColors.error }} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const { modo: mModo, item: mItem } = modal;
  const tipoActual = form.getFieldValue('tipo') || tab;

  const tabItems = ['rastreo', 'vision'].map(t => ({
    key: t,
    label: t === 'rastreo' ? 'Rastreo Satelital' : 'HSS Vision',
    children: (
      <Table columns={columns} dataSource={filtrar(t)} rowKey="id"
        loading={loading} scroll={{ x: 'max-content' }}
        pagination={{
          current: paginas[t],
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, size) => { setPaginas(prev => ({ ...prev, [t]: p })); setPageSize(size); },
          showTotal: total => `${total} líneas`,
        }}
      />
    ),
  }));

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MobileOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Líneas SIM</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>
                Gestión de líneas — Rastreo Satelital · HSS Vision
              </p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirModal('nuevo')} size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nueva línea
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats — fila 1: estado */}
        <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
          {[
            { label: 'Total', value: stats.total, color: hssColors.primary, icon: <MobileOutlined /> },
            { label: 'Instaladas', value: stats.activas, color: hssColors.success, icon: <CheckCircleOutlined /> },
            { label: 'En Stock', value: stats.inactivas, color: hssColors.warning, icon: <StopOutlined /> },
            { label: 'Dadas de baja', value: stats.bajas, color: hssColors.error, icon: <CloseCircleOutlined /> },
          ].map(s => (
            <Col span={6} key={s.label}>
              <Card loading={loading} style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm }}>
                <Statistic title={s.label} value={s.value} valueStyle={{ color: s.color, fontWeight: 700 }}
                  prefix={React.cloneElement(s.icon, { style: { color: s.color } })} />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Stats — fila 2: operadora + tipo SIM */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Claro',     value: stats.claro,    color: '#e74c3c' },
            { label: 'Tigo',      value: stats.tigo,     color: '#2980b9' },
            { label: 'Personal',  value: stats.personal, color: '#16a085' },
            { label: 'Voz',       value: stats.voz,      color: hssColors.success },
            { label: 'M2M / IoT', value: stats.m2m,      color: '#8e44ad' },
          ].map(s => (
            <Card key={s.label} loading={loading}
              style={{ flex: '1 1 160px', borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowSm, borderLeft: `4px solid ${s.color}` }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: hssColors.textSecondary, marginTop: 4 }}>{s.label}</div>
              </div>
            </Card>
          ))}
        </div>

        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <Input placeholder="Buscar por número, ICCID, operadora, vehículo, dispositivo..."
              prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
              style={{ width: 460, borderRadius: 8 }} size="large"
              value={search} onChange={e => { setSearch(e.target.value); setPaginas({ rastreo: 1, vision: 1 }); }} allowClear />
            <Button icon={<DownloadOutlined />} size="large" style={{ borderRadius: 8 }} onClick={exportarExcel}>
              Exportar Excel ({lineas.length} líneas)
            </Button>
          </div>
          <Tabs activeKey={tab} onChange={t => { setTab(t); setSearch(''); }} items={tabItems} />
        </Card>
      </div>

      {/* Modal crear / editar / asignar */}
      <Modal
        title={
          <Space style={{ fontSize: 16, fontWeight: 700, color: hssColors.primary }}>
            <MobileOutlined />
            <span>
              {mModo === 'nuevo' ? 'Nueva línea SIM'
                : mModo === 'editar' ? `Editar línea — ${mItem?.numero || mItem?.iccid}`
                : `Asignar — ${mItem?.numero || mItem?.iccid}`}
            </span>
          </Space>
        }
        open={!!mModo}
        onCancel={cerrarModal}
        footer={null}
        width={520}
      >
        <Form form={form} onFinish={onSubmit} layout="vertical" style={{ marginTop: 20 }}>

          {/* Asignar */}
          {mModo === 'asignar' && (
            <>
              <Form.Item name="vehiculo_chapa" label="Vehículo (dejar vacío para desasignar)">
                <Select size="large" showSearch allowClear placeholder="Buscar chapa..." optionFilterProp="label">
                  {vehiculos.map(v => <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo}`}>{v.chapa} — {v.marca} {v.modelo}</Option>)}
                </Select>
              </Form.Item>
              {mItem?.tipo === 'rastreo' ? (
                <Form.Item name="gps_id" label="Dispositivo GPS (dejar vacío para desasignar)">
                  <Select size="large" showSearch allowClear placeholder="Buscar IMEI..." optionFilterProp="label">
                    {gpsList.map(g => <Option key={g.id} value={g.id} label={`${g.imei} — ${g.marca} ${g.modelo}`}>{g.imei} — {g.marca} {g.modelo}</Option>)}
                  </Select>
                </Form.Item>
              ) : (
                <Form.Item name="router_id" label="Router (dejar vacío para desasignar)">
                  <Select size="large" showSearch allowClear placeholder="Buscar serie..." optionFilterProp="label">
                    {routers.map(r => <Option key={r.id} value={r.id} label={`${r.numero_serie} — ${r.marca}`}>{r.numero_serie} — {r.marca}</Option>)}
                  </Select>
                </Form.Item>
              )}
              <Form.Item name="observacion" label="Observación (opcional)">
                <Input size="large" style={{ borderRadius: 8 }} placeholder="Ej: instalación en unidad nueva" />
              </Form.Item>
            </>
          )}

          {/* Nuevo / Editar */}
          {mModo !== 'asignar' && (
            <>
              <Form.Item name="numero" label="Número de línea" rules={[{ required: false }]}>
                <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="Ej: 0981123456 (opcional si solo hay ICCID)" />
              </Form.Item>
              <Form.Item name="iccid" label="ICCID / N° serie de la tarjeta SIM">
                <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="Ej: 89595..." />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
                  <Select size="large">
                    <Option value="rastreo">Rastreo Satelital</Option>
                    <Option value="vision">HSS Vision</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="operadora" label="Operadora" rules={[{ required: true }]}>
                  <Select size="large">
                    <Option value="Claro"><Tag color="red">Claro</Tag></Option>
                    <Option value="Tigo"><Tag color="blue">Tigo</Tag></Option>
                    <Option value="Personal"><Tag color="cyan">Personal</Tag></Option>
                  </Select>
                </Form.Item>
                <Form.Item name="cobertura" label="Cobertura">
                  <Select size="large" allowClear placeholder="Solo para rastreo">
                    <Option value="nacional">Nacional</Option>
                    <Option value="internacional">Internacional (roaming)</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="estado" label="Estado">
                  <Select size="large">
                    <Option value="instalado">Instalado</Option>
                    <Option value="stock">En Stock</Option>
                    <Option value="baja">Baja</Option>
                  </Select>
                </Form.Item>
              </div>
            </>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={cerrarModal} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                {mModo === 'asignar' ? 'Confirmar asignación' : mModo === 'editar' ? 'Guardar cambios' : 'Crear línea'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer historial */}
      <Drawer
        title={historialData ? `Historial — ${historialData.linea.numero}` : 'Historial'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={420}
      >
        {loadingHistorial && <p>Cargando...</p>}
        {historialData && !loadingHistorial && (
          <>
            <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: hssColors.primary }}>{historialData.linea.numero}</div>
              <div style={{ fontSize: 12, color: hssColors.textSecondary }}>
                <Tag color={OPERADORA_COLOR[historialData.linea.operadora]}>{historialData.linea.operadora}</Tag>
                <Tag color={ESTADO_COLOR[historialData.linea.estado]}>{ESTADO_LABEL[historialData.linea.estado]}</Tag>
              </div>
            </div>
            {historialData.historial.length === 0
              ? <p style={{ color: hssColors.textSecondary }}>Sin movimientos registrados.</p>
              : (
                <Timeline
                  items={historialData.historial.map(h => ({
                    color: EVENTO_COLOR[h.evento] || 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{h.evento}</div>
                        {h.vehiculo_nuevo && <div style={{ fontSize: 12 }}>Vehículo: <b>{h.vehiculo_nuevo}</b></div>}
                        {h.vehiculo_anterior && h.vehiculo_anterior !== h.vehiculo_nuevo &&
                          <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Anterior: {h.vehiculo_anterior}</div>}
                        {h.dispositivo_nuevo && <div style={{ fontSize: 12 }}>Dispositivo: <b style={{ fontFamily: 'monospace' }}>{h.dispositivo_nuevo}</b></div>}
                        {h.dispositivo_anterior && h.dispositivo_anterior !== h.dispositivo_nuevo &&
                          <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Anterior: {h.dispositivo_anterior}</div>}
                        {h.observacion && <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Obs: {h.observacion}</div>}
                        <div style={{ fontSize: 11, color: hssColors.gray400 }}>
                          {h.fecha ? new Date(h.fecha).toLocaleString('es-PY') : ''} — {h.usuario_nombre}
                        </div>
                      </div>
                    ),
                  }))}
                />
              )
            }
          </>
        )}
      </Drawer>

      <Footer />
    </div>
  );
};

export default Lineas;
